import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DATE_LOCALE, MatNativeDateModule } from '@angular/material/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgSelectModule } from '@ng-select/ng-select';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { JwtInterceptor } from './helpers/jwt.interceptor';
import { LoadingInterceptor } from './helpers/loading.interceptor';
import { NgxPaginationModule } from 'ngx-pagination';
import { AppRoutingModule } from './app-routing.module';

import { AuthService } from "./services/auth";

import { AppComponent } from './app.component';
import { LoadingOverlayComponent } from './components/loading-overlay/loading-overlay.component';
import { TopbarComponent } from './components/topbar/topbar.component';
import { SidebarComponent } from './components/sidebar/sidebar.component';
import { ErrorComponent } from './components/error/error.component';
import { DashboardComponent } from './components/dashboard/dashboard.component';
import { LoginComponent } from './components/login/login.component';
import { UserDeactiveModalComponent } from './modals/user/user-deactive-modal/user-deactive-modal.component';
import { UserActiveModalComponent } from './modals/user/user-active-modal/user-active-modal.component';
import { UserDeleteModalComponent } from './modals/user/user-delete-modal/user-delete-modal.component';

import { UsernameFilterPipe } from './pipes/user/username-filter.pipe';
import { UserFilterPipe } from './pipes/user/user-filter.pipe';
import { CompanyNameFilterPipe } from './pipes/request/company-name-filter.pipe';
import { ProgressStatusFilterPipe } from './pipes/request/progress-status-filter.pipe';
import { EmailFilterPipe } from './pipes/request/email-filter.pipe';
import { RequestTypeFilterPipe } from './pipes/request/request-type-filter.pipe';
import { MobilePhoneFilterPipe } from './pipes/request/mobile-phone-filter.pipe';
import { IdFilterPipe } from './pipes/request/id-filter.pipe';
import { CreationDateFilterPipe } from './pipes/request/creation-date-filter.pipe';
import { GlobalSearchFilterPipe } from './pipes/request/global-search-filter.pipe';
import { DnsProblemFilterPipe } from './pipes/request/dns-problem-filter.pipe';
import { IperProblemFilterPipe } from './pipes/request/iper-problem-filter.pipe';
import { InternalManagementFilterPipe } from './pipes/request/internal-management-filter.pipe';

import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { GeneralModalComponent } from './modals/general/general-modal/general-modal.component';
import { RequestComponent } from './components/request/request.component';
import { RequestCompleteModalComponent } from './modals/request/request-complete-modal/request-complete-modal.component';
import { RequestUncompleteModalComponent } from './modals/request/request-uncomplete-modal/request-uncomplete-modal.component';
import { RequestCreateModalComponent } from './modals/request/request-create-modal/request-create-modal.component';
import { RequestDetailComponent } from './components/request-detail/request-detail.component';
import { RequestDeleteModalComponent } from './modals/request/request-delete-modal/request-delete-modal.component';
import { RequestUpdateComponent } from './components/request-update/request-update.component';
import { RequestInfoPaymentModalComponent } from './modals/request/request-info-payment-modal/request-info-payment-modal.component';
import { RequestCompileComponent } from './components/request-compile/request-compile.component';
import { VideoModalComponent } from './modals/video/video-modal/video-modal.component';
import { DocumentPreviewModalComponent } from './modals/document/document-preview-modal/document-preview-modal.component';
import { DocumentPreviewPaneComponent } from './components/document-preview-pane/document-preview-pane.component';
import { RequestRevenueStampModalComponent } from './modals/request/request-revenue-stamp-modal/request-revenue-stamp-modal.component';
import { CustomerComponent } from './components/customer/customer.component';
import { CustomerDetailComponent } from './components/customer-detail/customer-detail.component';
import { CustomerUpdateComponent } from './components/customer-update/customer-update.component';
import { CustomerCompanyNameFilterPipe } from './pipes/customer/company-name-filter.pipe';
import { CustomerVatNumberFilterPipe } from './pipes/customer/vat-number-filter.pipe';
import { CustomerIdFilterPipe } from './pipes/customer/id-filter.pipe';
import { CustomerCityFilterPipe } from './pipes/customer/city-filter.pipe';
import { CustomerEmailFilterPipe } from './pipes/customer/email-filter.pipe';
import { CustomerMobilePhoneFilterPipe } from './pipes/customer/mobile-phone-filter.pipe';
import { CustomerEnabledFilterPipe } from './pipes/customer/enabled-filter.pipe';
import { CustomerCreationDateFilterPipe } from './pipes/customer/creation-date-filter.pipe';
import { CustomerGlobalSearchFilterPipe } from './pipes/customer/global-search-filter.pipe';
import { CustomerCreateModalComponent } from './modals/customer/customer-create-modal/customer-create-modal.component';
import { RequestNotesComponent } from './components/request-notes/request-notes.component';
import { RequestNotesModalComponent } from './modals/request/request-notes-modal/request-notes-modal.component';
import { RequestAccessModalComponent } from './modals/request/request-access-modal/request-access-modal.component';
import { RequestNoteCreateModalComponent } from './modals/request/request-note-create-modal/request-note-create-modal.component';
import { NoteUsernameFilterPipe } from './pipes/note/username-filter.pipe';
import { NoteCreationDateFilterPipe } from './pipes/note/creation-date-filter.pipe';
import { NoteLastModificationDateFilterPipe } from './pipes/note/last-modification-date-filter.pipe';
import { NoteTextFilterPipe } from './pipes/note/text-filter.pipe';
import { SupplierComponent } from './components/supplier/supplier.component';
import { SupplierUpdateComponent } from './components/supplier-update/supplier-update.component';
import { SupplierDetailComponent } from './components/supplier-detail/supplier-detail.component';
import { SupplierCreateModalComponent } from './modals/supplier/supplier-create-modal/supplier-create-modal.component';
import { SupplierGlobalSearchFilterPipe } from './pipes/supplier/global-search-filter.pipe';
import { SupplierIdFilterPipe } from './pipes/supplier/id-filter.pipe';
import { SupplierNameFilterPipe } from './pipes/supplier/name-filter.pipe';
import { SupplierCfFilterPipe } from './pipes/supplier/cf-filter.pipe';
import { SupplierEmailFilterPipe } from './pipes/supplier/email-filter.pipe';
import { ReferrerComponent } from './components/referrer/referrer.component';
import { ReferrerUpdateComponent } from './components/referrer-update/referrer-update.component';
import { ReferrerDetailComponent } from './components/referrer-detail/referrer-detail.component';
import { ReferrerCreateModalComponent } from './modals/referrer/referrer-create-modal/referrer-create-modal.component';
import { ReferrerGlobalSearchFilterPipe } from './pipes/referrer/global-search-filter.pipe';
import { ReferrerIdFilterPipe } from './pipes/referrer/id-filter.pipe';
import { ReferrerNameFilterPipe } from './pipes/referrer/name-filter.pipe';
import { ReferrerSurnameFilterPipe } from './pipes/referrer/surname-filter.pipe';
import { ReferrerEmailFilterPipe } from './pipes/referrer/email-filter.pipe';
import { StableColumnsDirective } from './directives/stable-columns.directive';
import { AdvancedSearchComponent } from './components/advanced-search/advanced-search.component';
import { PresetFiltersComponent } from './components/preset-filters/preset-filters.component';
import { AdvancedSearchFilterPipe } from './pipes/advanced-search-filter.pipe';
import { CopyFieldComponent } from './components/copy-field/copy-field.component';
import { RequestDnsViewComponent } from './components/request-dns-view/request-dns-view.component';
import { RequestIperViewComponent } from './components/request-iper-view/request-iper-view.component';
import { CustomerEmailsEditorComponent } from './components/customer-emails-editor/customer-emails-editor.component';

@NgModule({
  declarations: [
    AppComponent,
    LoadingOverlayComponent,
    TopbarComponent,
    SidebarComponent,
    ErrorComponent,
    DashboardComponent,
    LoginComponent,
    UsernameFilterPipe,
    UserFilterPipe,
    CompanyNameFilterPipe,
    ProgressStatusFilterPipe,
    EmailFilterPipe,
    RequestTypeFilterPipe,
    MobilePhoneFilterPipe,
    IdFilterPipe,
    CreationDateFilterPipe,
    GlobalSearchFilterPipe,
    DnsProblemFilterPipe,
    IperProblemFilterPipe,
    InternalManagementFilterPipe,
    UserDeleteModalComponent,
    UserDeactiveModalComponent,
    UserActiveModalComponent,
    GeneralModalComponent,
    RequestComponent,
    RequestCompleteModalComponent,
    RequestUncompleteModalComponent,
    RequestCreateModalComponent,
    RequestDetailComponent,
    RequestDeleteModalComponent,
    RequestUpdateComponent,
    RequestInfoPaymentModalComponent,
    RequestCompileComponent,
    VideoModalComponent,
    DocumentPreviewModalComponent,
    DocumentPreviewPaneComponent,
    RequestRevenueStampModalComponent,
    CustomerComponent,
    CustomerDetailComponent,
    CustomerUpdateComponent,
    CustomerCompanyNameFilterPipe,
    CustomerVatNumberFilterPipe,
    CustomerIdFilterPipe,
    CustomerCityFilterPipe,
    CustomerEmailFilterPipe,
    CustomerMobilePhoneFilterPipe,
    CustomerEnabledFilterPipe,
    CustomerCreationDateFilterPipe,
    CustomerGlobalSearchFilterPipe,
    CustomerCreateModalComponent,
    RequestNotesComponent,
    RequestNotesModalComponent,
    RequestAccessModalComponent,
    RequestNoteCreateModalComponent,
    NoteUsernameFilterPipe,
    NoteCreationDateFilterPipe,
    NoteLastModificationDateFilterPipe,
    NoteTextFilterPipe,
    SupplierComponent,
    SupplierUpdateComponent,
    SupplierDetailComponent,
    SupplierCreateModalComponent,
    SupplierGlobalSearchFilterPipe,
    SupplierIdFilterPipe,
    SupplierNameFilterPipe,
    SupplierCfFilterPipe,
    SupplierEmailFilterPipe,
    ReferrerComponent,
    ReferrerUpdateComponent,
    ReferrerDetailComponent,
    ReferrerCreateModalComponent,
    ReferrerGlobalSearchFilterPipe,
    ReferrerIdFilterPipe,
    ReferrerNameFilterPipe,
    ReferrerSurnameFilterPipe,
    ReferrerEmailFilterPipe,
    StableColumnsDirective,
    AdvancedSearchComponent,
    PresetFiltersComponent,
    AdvancedSearchFilterPipe,
    CopyFieldComponent,
    RequestDnsViewComponent,
    RequestIperViewComponent,
    CustomerEmailsEditorComponent
  ],
  imports: [
    BrowserModule,
    FormsModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatCheckboxModule,
    MatTooltipModule,
    MatRadioModule,
    MatSelectModule,
    NgSelectModule,
    HttpClientModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    NgxPaginationModule,
    NgbModule
  ],
  entryComponents: [
    UserActiveModalComponent,
    UserDeactiveModalComponent,
    UserDeleteModalComponent
  ],
  providers: [
    AuthService,
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LoadingInterceptor, multi: true },
    { provide: MAT_DATE_LOCALE, useValue: 'it-IT' }
  ],

  bootstrap: [AppComponent]
})
export class AppModule { }
